import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AutocompleteAddressInput } from "./AutocompleteAddressInput";
import { TimeDialSelector } from "./TimeDialSelector";
import { LocationMapPreview } from "@/components/rider/landing/LocationMapPreview";
import { getAssetPath } from "@/utils/assetUtils";
import { API_URL } from "@/config/api";

interface AddressInputStepProps {
  from: string;
  setFrom: (value: string) => void;
  startTime: string;
  handleStartTimeChange: (time: string) => void;
  onNext: () => void;
  isRoundTrip?: boolean;
  returnFrom?: string;
  setReturnFrom?: (value: string) => void;
  returnStartTime?: string;
  handleReturnStartTimeChange?: (time: string) => void;
  returnToPickup?: boolean;
  setReturnToPickup?: (value: boolean) => void;
  returnTo?: string;
  setReturnTo?: (value: string) => void;
  timezoneAbbreviation?: string;
}

export const AddressInputStep = ({
  from,
  setFrom,
  startTime,
  handleStartTimeChange,
  onNext,
  isRoundTrip = false,
  returnFrom = "",
  setReturnFrom = () => {},
  returnStartTime = "",
  handleReturnStartTimeChange = () => {},
  returnToPickup = false,
  setReturnToPickup = () => {},
  returnTo = "",
  setReturnTo = () => {},
  timezoneAbbreviation
}: AddressInputStepProps) => {
  const [selectedCoordinates, setSelectedCoordinates] = useState<{latitude: number, longitude: number} | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<{place_id: string, description: string} | null>(null);

  const canProceed = from.trim() !== "" && startTime.trim() !== "" && 
    (!isRoundTrip || true); // For round trips, we only need the way there fields

  // Handle address selection and coordinate extraction
  const handleFromAddressChange = async (value: string) => {
    setFrom(value);
    
    // If this looks like a complete address selection, try to get coordinates
    if (value && value.includes(',')) {
      try {
        // Search for places to get coordinates
        const response = await fetch(`${API_URL}places/search/?q=${encodeURIComponent(value)}`);
        if (response.ok) {
          const data = await response.json();
          console.log("Address search response:", data);
          
          if (Array.isArray(data) && data.length > 0) {
            const place = data[0];
            setSelectedPlace({
              place_id: place.place_id,
              description: place.description
            });
            
            // Check for geometry in geocode array
            if (place.geocode && place.geocode[0]?.geometry?.location) {
              console.log("Found coordinates in search results:", place.geocode[0].geometry.location);
              setSelectedCoordinates({
                latitude: place.geocode[0].geometry.location.lat,
                longitude: place.geocode[0].geometry.location.lng
              });
            } else {
              // Try to get coordinates from details endpoint
              console.log("No coordinates in search, trying details endpoint...");
              const detailsResponse = await fetch(`${API_URL}places/details/?place_id=${place.place_id}`);
              if (detailsResponse.ok) {
                const detailsData = await detailsResponse.json();
                console.log("Details response:", detailsData);
                
                if (detailsData.geocode && detailsData.geocode[0]?.geometry?.location) {
                  setSelectedCoordinates({
                    latitude: detailsData.geocode[0].geometry.location.lat,
                    longitude: detailsData.geocode[0].geometry.location.lng
                  });
                } else if (detailsData.geometry?.location) {
                  setSelectedCoordinates({
                    latitude: detailsData.geometry.location.lat,
                    longitude: detailsData.geometry.location.lng
                  });
                } else {
                  console.log("No coordinates found, using default");
                  setSelectedCoordinates({ latitude: 34.0522, longitude: -118.2437 });
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching coordinates:", error);
        // Fallback to default coordinates
        setSelectedCoordinates({ latitude: 34.0522, longitude: -118.2437 });
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-white p-6">
      <div className="flex-1 flex flex-col justify-center">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-black mb-2">You're almost</h2>
          <h2 className="text-2xl font-bold text-black">ready to party 🎉</h2>
        </div>
        
        <div className="bg-gray-50 rounded-2xl p-6 space-y-6 -mx-[10px]">
          {/* Way There Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-3">
              Where should we pick you up? <span className="text-red-500">*</span>
            </h3>
            <AutocompleteAddressInput
              value={from}
              onChange={handleFromAddressChange}
              placeholder="Enter Address or Location *"
              className={`h-12 border-0 text-gray-800 placeholder:text-gray-500 rounded-xl mb-4 ${from.trim() ? 'bg-gray-200' : 'bg-white'}`}
            />
          </div>
          
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-700 mb-3 whitespace-nowrap">
                  What time should we pick you up? <span className="text-red-500">*</span>
                </h3>
                <TimeDialSelector
                  value={startTime}
                  onChange={handleStartTimeChange}
                  placeholder="Select When You'd Like To Be Picked Up"
                />
              </div>
              {timezoneAbbreviation && (
                <span className="text-sm text-gray-500 font-medium self-end mb-3">{timezoneAbbreviation}</span>
              )}
            </div>

          {/* Location Map Preview - show after address and time selection */}
          {from && selectedCoordinates && (
            <div>
              <LocationMapPreview
                locationName={selectedPlace?.description?.split(',')[0] || from.split(',')[0]}
                latitude={selectedCoordinates.latitude}
                longitude={selectedCoordinates.longitude}
                className="w-full"
              />
            </div>
          )}

          {/* Round Trip Drop-off Options - placed after Way There fields */}
          {isRoundTrip && (
            <>
              {/* Checkbox for returning to pickup location */}
              <div className="flex items-center space-x-3 pt-4">
                <Checkbox 
                  id="return-to-pickup" 
                  checked={returnToPickup} 
                  onCheckedChange={(checked) => setReturnToPickup(checked === true)} 
                  className="border-gray-400 data-[state=checked]:bg-black data-[state=checked]:text-white" 
                />
                <label htmlFor="return-to-pickup" className="text-sm text-gray-700 cursor-pointer font-medium">
                  Drop off back at {from || "Pickup Address"}
                </label>
              </div>

              {!returnToPickup && (
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-3">Or</h3>
                  <h3 className="text-lg font-medium text-gray-700 mb-3">Choose Different Drop off Address</h3>
                  <AutocompleteAddressInput
                    value={returnTo}
                    onChange={setReturnTo}
                    placeholder="Enter Different Drop off Address"
                    className="h-12 bg-gray-200 border-0 text-gray-800 placeholder:text-gray-500 rounded-xl"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      <div className="mt-8">
        <Button
          onClick={onNext}
          disabled={!canProceed}
          className="w-full bg-gradient-to-r from-cyan-400 to-green-500 text-white py-4 rounded-full text-lg font-semibold hover:from-cyan-500 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Confirm Trip Details
        </Button>
      </div>
    </div>
  );
};
